import React from 'react';
import { useLocation } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Input, Label, Textarea, Button, Switch } from '@artstorefronts/ui';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { toast } from 'sonner';
import { Frown, Plus, Smile, Wand2, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CategorySelector } from '../CategorySelector';
import { SocialLinkField } from './SocialLinkField';
import { LocationSelector } from './LocationSelector';
import {
  USERNAME_ONLY_PLATFORMS,
  extractUsernameFromUrl,
  prepareSocialLinkForApi as prepareSocialLinkForApiUtil,
  constructUrlFromUsername,
} from '../../utils/socialLinks';
import { useAuth } from '../../context/auth';
import { UPDATE_USER, UPDATE_PROFILE } from '../../graphql/mutations';
import { CHECK_SLUG_AVAILABILITY, GET_CATEGORIES } from '../../graphql/queries';
import { useAnalytics } from '../../context/analytics';
import { ImageUploadField } from './ImageUploadField';
import { WatermarkUploader } from './WatermarkUploader';
import { getPublicSiteBaseUrl } from '../../utils';
import { isValidUrl } from '../../utils/url';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ARTIST_STATEMENT_MAX_LENGTH,
  BIO_MAX_LENGTH,
  STORY_MAX_LENGTH,
  FIRST_NAME_SCHEMA,
  LAST_NAME_SCHEMA,
  bioSchema,
  DISPLAY_NAME_SCHEMA,
  ARTIST_STATEMENT_SCHEMA,
  COUNTRY_SCHEMA,
  REGION_SCHEMA,
  CITY_SCHEMA,
  COUNTRY_CODE_SCHEMA,
  REGION_CODE_SCHEMA,
  LATITUDE_SCHEMA,
  LONGITUDE_SCHEMA,
  TIMEZONE_SCHEMA,
  SLUG_SCHEMA,
  AVATAR_URL_SCHEMA,
  BANNER_URL_SCHEMA,
  CATEGORIES_SCHEMA,
  storySchema,
} from '~/utils/user';
import CompactAudioRecorder from '~/pages/onboarding/components/CompactAudioRecorder';
import CopyButton from '../CopyButton';
import ContentGenerationPopover from '../ContentGenerationPopover';
import { useProfileGeneration } from '../../utils/profileGeneration';
import { matchGeneratedCategories } from '../../utils/matchGeneratedCategories';

// Validates facebook social links; returns a boolean verdict or undefined to
// fall through to the generic username-only validation below.
function validateFacebook(data: { url?: string; username?: string }): boolean | undefined {
  // Check if username is in profile.php format
  if (data.username && data.username.startsWith('profile.php?id=')) {
    const idValue = data.username.replace('profile.php?id=', '');
    if (!idValue || !idValue.trim()) {
      return false;
    }
    // Construct the full URL and validate it
    const constructedUrl = `https://facebook.com/profile.php?id=${idValue.trim()}`;
    return isValidUrl(constructedUrl);
  }
  // Check if URL is a profile.php URL
  if (data.url && !data.username) {
    try {
      const urlObj = new URL(data.url.trim());
      if (urlObj.pathname === '/profile.php' && urlObj.searchParams.has('id')) {
        return isValidUrl(data.url);
      }
    } catch {
      // If URL parsing fails, continue with normal validation
    }
  }
  return undefined;
}

// Social link schema using the cleaner pattern
const SocialLinkSchema = z
  .object({
    type: z.string().optional(),
    url: z.string().optional(),
    username: z.string().optional(),
    title: z.string().optional(),
    locked: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If type is provided
      if (data.type && data.type.length > 0) {
        const isUsernameOnly = USERNAME_ONLY_PLATFORMS.has(data.type);

        // Always allow empty values - only validate if there's actual content
        if (isUsernameOnly) {
          // Facebook profile.php URLs with id parameter should validate the URL directly
          // Also handle when username is in format "profile.php?id=..." (user-entered format)
          const facebookResult = data.type === 'facebook' ? validateFacebook(data) : undefined;
          if (facebookResult !== undefined) {
            return facebookResult;
          }
          // For username-only platforms, only validate if username is provided
          if (data.username && data.username.trim().length > 0) {
            // Check if username contains @ (should be removed)
            if (data.username.includes('@')) {
              return false;
            }
            // For Bluesky, check that username contains .bsky.social
            if (data.type === 'bluesky' && !data.username.trim().endsWith('.bsky.social')) {
              return false;
            }
            // Construct full URL from username and validate it
            const fullUrl = constructUrlFromUsername(data.username.trim(), data.type);
            if (fullUrl) {
              return isValidUrl(fullUrl);
            }
            return false;
          }
          // Empty username is valid
          return true;
        } else {
          // For website/other, only validate if URL is provided
          if (data.url && data.url.trim().length > 0) {
            return isValidUrl(data.url);
          }
          // Empty URL is valid
          return true;
        }
      }
      // If no type, empty URL/username is allowed
      return true;
    },
    (data) => {
      // Custom error messages based on platform type
      const isUsernameOnly = data.type && USERNAME_ONLY_PLATFORMS.has(data.type);

      if (isUsernameOnly) {
        if (data.username && data.username.includes('@')) {
          return {
            message: 'Please remove the @ symbol from your username',
            path: ['username'],
          };
        }
        if (data.type === 'bluesky' && data.username && !data.username.trim().endsWith('.bsky.social')) {
          return {
            message: 'Bluesky username must end with .bsky.social (e.g., username.bsky.social)',
            path: ['username'],
          };
        }
        return {
          message: 'Please enter a valid username',
          path: ['username'],
        };
      } else {
        return {
          message: 'Please enter a valid URL (e.g., https://example.com)',
          path: ['url'],
        };
      }
    }
  );

/**
 * The form submits every field at once, so the bio and story caps have to
 * tolerate the values already stored on this profile: a row written before a cap
 * existed would otherwise fail zod resolution and lock the editor out of saving
 * anything. The stored value shows as over-limit (the counters turn red) but is
 * never truncated, and any edit to it is validated against the cap as usual.
 */
const buildProfileSettingsSchema = (stored: { bio?: string | null; story?: string | null }) =>
  z.object({
    firstName: FIRST_NAME_SCHEMA,
    lastName: LAST_NAME_SCHEMA,
    story: storySchema(stored.story),
    bio: bioSchema(stored.bio),
    displayName: DISPLAY_NAME_SCHEMA,
    artistStatement: ARTIST_STATEMENT_SCHEMA,
    country: COUNTRY_SCHEMA,
    region: REGION_SCHEMA,
    city: CITY_SCHEMA,
    countryCode: COUNTRY_CODE_SCHEMA,
    regionCode: REGION_CODE_SCHEMA,
    latitude: LATITUDE_SCHEMA,
    longitude: LONGITUDE_SCHEMA,
    timezone: TIMEZONE_SCHEMA,
    slug: SLUG_SCHEMA,
    avatarUrl: AVATAR_URL_SCHEMA,
    bannerUrl: BANNER_URL_SCHEMA,
    categories: CATEGORIES_SCHEMA.optional(),
    socialLinks: z.array(SocialLinkSchema).optional(),
    isPublic: z.boolean().optional(),
    watermarkEnabled: z.boolean().optional(),
    showMockups: z.boolean().optional(),
    showArtworkSpecs: z.boolean().optional(),
    showActivity: z.boolean().optional(),
  });

type ProfileSettingsData = z.infer<ReturnType<typeof buildProfileSettingsSchema>>;

interface ProfileSettingsProps {
  user: any;
  onSave?: (values: ProfileSettingsData) => Promise<void>;
  onFormChange?: (isDirty: boolean, hasErrors: boolean) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
  resetFunctions?: React.MutableRefObject<{ [key: string]: (() => void) | null }>;
  isAdminMode?: boolean;
  refetchProfile?: () => Promise<void>;
}

const HM_LOGO_URL =
  'https://arthelper-production-public-fxzecshe.s3.us-east-1.amazonaws.com/assets/human-made-art-logo.png';

const ProfileSettings = React.memo(
  React.forwardRef<HTMLFormElement, ProfileSettingsProps>(
    (
      { user, onSave, onFormChange, onLoadingStateChange, resetFunctions, isAdminMode = false, refetchProfile },
      ref
    ) => {
      const { t } = useTranslation('settings');
      const { t: tc } = useTranslation('common');

      // Always call hooks, but conditionally use them
      const location = useLocation();
      const authContext = useAuth();
      const analyticsContext = useAnalytics();

      const currentUser = !isAdminMode ? authContext?.user : null;
      const refetchProfileFromAuth = !isAdminMode ? authContext?.refetchProfile : null;
      const profileRefetch = isAdminMode ? refetchProfile : refetchProfileFromAuth;
      const amplitude = !isAdminMode ? analyticsContext?.amplitude : null;
      const intercom = !isAdminMode ? analyticsContext?.intercom : null;
      const [updateUser] = useMutation(UPDATE_USER);
      const [updateProfile] = useMutation(UPDATE_PROFILE);
      const [baselineValues, setBaselineValues] = React.useState<any>(null);
      const { generateDisplayName, generateBio, generateStory, generateCategories, generateArtistStatement } =
        useProfileGeneration();

      // Generation popover states
      const [displayNamePopoverOpen, setDisplayNamePopoverOpen] = React.useState(false);
      const [bioPopoverOpen, setBioPopoverOpen] = React.useState(false);
      const [storyPopoverOpen, setStoryPopoverOpen] = React.useState(false);
      const [artistStatementPopoverOpen, setArtistStatementPopoverOpen] = React.useState(false);
      const [isGeneratingCategories, setIsGeneratingCategories] = React.useState(false);
      const [categoryModalOpen, setCategoryModalOpen] = React.useState(false);

      // Get available categories for matching
      const { data: categoriesData } = useQuery(GET_CATEGORIES);
      const availableCategories = React.useMemo(() => categoriesData?.categories || [], [categoriesData]);

      // Cache for temporary image URLs to prevent reloading
      const [tempImageUrls, setTempImageUrls] = React.useState<{
        avatarUrl?: string;
        bannerUrl?: string;
      }>({});

      // Slug validation
      const [checkSlugAvailability] = useLazyQuery(CHECK_SLUG_AVAILABILITY);
      const [slugValidation, setSlugValidation] = React.useState<{
        isValid: boolean;
        isChecking: boolean;
        error?: string;
      }>({ isValid: true, isChecking: false });

      // Helper function to ensure we always have website and instagram links
      const getDefaultSocialLinks = React.useCallback((existingLinks: any[] = []) => {
        const hasWebsite = existingLinks.some((link: any) => link.type === 'website');
        const hasInstagram = existingLinks.some((link: any) => link.type === 'instagram');

        // Process existing links to extract usernames from URLs
        const processedLinks = existingLinks.map((link: any) => {
          if (!link.type || !link.url) {
            return link;
          }

          const isUsernameOnly = USERNAME_ONLY_PLATFORMS.has(link.type);
          if (isUsernameOnly) {
            // Facebook profile.php URLs with id parameter should preserve the full URL
            // Extract the id parameter value to display in the username field
            if (link.type === 'facebook') {
              try {
                const urlObj = new URL(link.url);
                if (urlObj.pathname === '/profile.php' && urlObj.searchParams.has('id')) {
                  // Extract the id parameter value to show in the username field
                  const idValue = urlObj.searchParams.get('id') || '';
                  return {
                    ...link,
                    username: idValue ? `profile.php?id=${idValue}` : '',
                    url: link.url,
                  };
                }
              } catch {
                // If URL parsing fails, continue with normal extraction
              }
            }
            // Extract username from URL
            const username = extractUsernameFromUrl(link.url, link.type);
            return {
              ...link,
              username: username || '',
              // Keep URL for backward compatibility during transition
              url: link.url,
            };
          }

          // For website/other, keep URL as-is
          return link;
        });

        const socialLinks = [...processedLinks];

        // Add website link first if missing
        if (!hasWebsite) {
          socialLinks.unshift({ type: 'website', url: '', title: '' });
        }

        // Add instagram link second if missing
        if (!hasInstagram) {
          const websiteIndex = socialLinks.findIndex((link: any) => link.type === 'website');
          socialLinks.splice(websiteIndex + 1, 0, { type: 'instagram', url: '', username: '', title: '' });
        }

        return socialLinks;
      }, []);

      // Helper function to prepare social link for API submission (removes frontend-only properties)
      const prepareSocialLinkForApi = (link: any) => {
        return prepareSocialLinkForApiUtil(link);
      };

      // Helper function to create form values object
      const createFormValues = React.useCallback(
        (userData: any) => {
          // In admin mode, userData is the profile object directly
          // In regular mode, userData is the user object with nested profile
          const profileData = isAdminMode ? userData : userData?.profile;
          const userInfo = isAdminMode ? userData?.user : userData;

          return {
            firstName: userInfo?.firstName,
            lastName: userInfo?.lastName,
            story: userInfo?.story,
            bio: profileData?.bio,
            displayName: profileData?.displayName,
            artistStatement: profileData?.artistStatement,
            country: profileData?.country,
            region: profileData?.region,
            city: profileData?.city,
            countryCode: profileData?.countryCode,
            regionCode: profileData?.regionCode,
            latitude: profileData?.latitude,
            longitude: profileData?.longitude,
            timezone: profileData?.timezone,
            slug: profileData?.slug,
            avatarUrl: profileData?.avatarUrl,
            bannerUrl: profileData?.bannerUrl,
            categories: (profileData?.categories || []).map((cat: any) => ({
              categoryId: cat.categoryId || cat.id,
              isPrimary: cat.isPrimary || false,
            })),
            socialLinks: getDefaultSocialLinks(profileData?.socialLinks),
            isPublic: profileData?.isPublic !== undefined ? profileData.isPublic : true,
            watermarkEnabled: profileData?.watermarkEnabled !== undefined ? profileData.watermarkEnabled : false,
            showMockups: profileData?.showMockups !== undefined ? profileData.showMockups : true,
            showArtworkSpecs: profileData?.showArtworkSpecs !== undefined ? profileData.showArtworkSpecs : true,
            showActivity: profileData?.showActivity !== undefined ? profileData.showActivity : true,
          };
        },
        [getDefaultSocialLinks, isAdminMode]
      );

      // Rebuilt when the loaded profile changes so the caps stay anchored to the
      // stored bio and story (see `buildProfileSettingsSchema`).
      const storedBio = (isAdminMode ? user?.bio : user?.profile?.bio) as string | null | undefined;
      const storedStory = (isAdminMode ? user?.user?.story : user?.story) as string | null | undefined;
      const resolver = React.useMemo(
        () => zodResolver(buildProfileSettingsSchema({ bio: storedBio, story: storedStory })),
        [storedBio, storedStory]
      );

      const {
        register,
        handleSubmit,
        watch,
        reset,
        setValue,
        control,
        trigger,
        formState: { errors, isDirty },
      } = useForm<ProfileSettingsData>({
        defaultValues: createFormValues(user),
        resolver,
        mode: 'onBlur',
      });

      // Category generation handler (defined after form hooks)
      const handleGenerateCategories = React.useCallback(async () => {
        const eventProperties = {
          input_field: 'profile_categories',
          has_suggestion: false,
          has_previous_content: !!watch('categories')?.length,
        };
        amplitude?.track('content_generation_triggered', eventProperties);
        intercom?.trackEvent('content_generation_triggered', eventProperties);

        setIsGeneratingCategories(true);
        setCategoryModalOpen(true);

        try {
          const generatedCategoryNames = await generateCategories();

          const matchedCategories = matchGeneratedCategories(availableCategories, generatedCategoryNames, 6);

          if (matchedCategories.length > 0) {
            const formattedCategories = matchedCategories.map((cat: any, index: number) => ({
              categoryId: cat.id,
              isPrimary: index === 0, // First category is primary
            }));

            // Merge with existing categories, avoiding duplicates
            const currentCategories = watch('categories') || [];
            const existingIds = new Set(currentCategories.map((c: any) => c.categoryId));
            const newCategories = formattedCategories.filter((c: any) => !existingIds.has(c.categoryId));

            if (newCategories.length > 0) {
              const updatedCategories = [...currentCategories, ...newCategories];
              setValue('categories', updatedCategories, { shouldDirty: true });
            }
          }
        } catch (error) {
          console.error('Error generating categories:', error);
          toast.error(t('profile.categories.generate-error'));
        } finally {
          setIsGeneratingCategories(false);
        }
      }, [amplitude, intercom, generateCategories, availableCategories, watch, setValue]);

      const {
        fields: socialLinkFields,
        append: appendSocialLink,
        remove: removeSocialLink,
      } = useFieldArray({
        control,
        name: 'socialLinks',
      });

      // Handle form reset
      const handleReset = React.useCallback(() => {
        if (baselineValues) {
          reset(baselineValues);
        } else {
          reset(createFormValues(user));
        }
        // Clear temp image URLs on reset
        setTempImageUrls({});
      }, [user, reset, createFormValues, baselineValues]);

      // Register reset function with context
      React.useEffect(() => {
        const currentResetFunctions = resetFunctions?.current;
        if (currentResetFunctions) {
          currentResetFunctions.settings = handleReset;
        }
        return () => {
          if (currentResetFunctions) {
            currentResetFunctions.settings = null;
          }
        };
      }, [handleReset, resetFunctions]);

      // Listen for reset events
      React.useEffect(() => {
        const formElement = ref as React.MutableRefObject<HTMLFormElement | null>;
        if (formElement?.current) {
          const handleFormReset = () => {
            handleReset();
          };
          formElement.current.addEventListener('formReset', handleFormReset);
          return () => {
            formElement.current?.removeEventListener('formReset', handleFormReset);
          };
        }
      }, [handleReset, ref]);

      // Reset form only on initial load when user data is first available
      React.useEffect(() => {
        if (user && !baselineValues) {
          reset(createFormValues(user));

          // Set baseline values only on initial load
          const profileData = isAdminMode ? user : user?.profile;
          const userInfo = isAdminMode ? user?.user : user;

          setBaselineValues({
            firstName: userInfo?.firstName || '',
            lastName: userInfo?.lastName || '',
            story: userInfo?.story || '',
            bio: profileData?.bio || '',
            displayName: profileData?.displayName || '',
            artistStatement: profileData?.artistStatement || '',
            country: profileData?.country || '',
            region: profileData?.region || '',
            city: profileData?.city || '',
            countryCode: profileData?.countryCode || '',
            regionCode: profileData?.regionCode || '',
            latitude: profileData?.latitude || null,
            longitude: profileData?.longitude || null,
            timezone: profileData?.timezone || '',
            slug: profileData?.slug || '',
            avatarUrl: profileData?.avatarUrl,
            bannerUrl: profileData?.bannerUrl,
            categories: (profileData?.categories || []).map((cat: any) => ({
              categoryId: cat.categoryId || cat.id,
              isPrimary: cat.isPrimary || false,
            })),
            socialLinks: getDefaultSocialLinks(profileData?.socialLinks),
            isPublic: profileData?.isPublic !== undefined ? profileData.isPublic : true,
            watermarkEnabled: profileData?.watermarkEnabled !== undefined ? profileData.watermarkEnabled : false,
            showMockups: profileData?.showMockups !== undefined ? profileData.showMockups : true,
            showArtworkSpecs: profileData?.showArtworkSpecs !== undefined ? profileData.showArtworkSpecs : true,
            showActivity: profileData?.showActivity !== undefined ? profileData.showActivity : true,
          });
        }
      }, [user, reset, baselineValues, createFormValues, getDefaultSocialLinks, isAdminMode]);

      // Sync isPublic when the loaded profile changes it out from under the form
      // (e.g. a save elsewhere, or the query refetching). This Switch is the only
      // place the client app sets visibility — the app header no longer carries a
      // public/private toggle.
      const profileData = isAdminMode ? user : user?.profile;
      const externalIsPublic = profileData?.isPublic !== undefined ? profileData.isPublic : true;
      React.useEffect(() => {
        if (baselineValues) {
          const currentFormValue = watch('isPublic');
          if (externalIsPublic !== currentFormValue) {
            setValue('isPublic', externalIsPublic, { shouldDirty: false });
          }
        }
      }, [externalIsPublic, baselineValues]);

      // Ensure categories are properly formatted after form reset
      const currentCategories = watch('categories');
      React.useEffect(() => {
        if (currentCategories && currentCategories.length > 0) {
          // Check if we have any primary category
          const hasPrimary = currentCategories.some((cat: any) => cat.isPrimary);

          // If no primary category is set, set the first one as primary
          if (!hasPrimary) {
            const updatedCategories = currentCategories.map((cat: any, index: number) => ({
              ...cat,
              isPrimary: index === 0, // Set first category as primary
            }));
            setValue('categories', updatedCategories);
          }
        }
      }, [currentCategories, setValue]);

      // Slug validation function
      const validateSlug = React.useCallback(
        async (slug: string) => {
          if (!slug || slug.length < 3) {
            setSlugValidation({ isValid: false, isChecking: false, error: t('profile.slug.min-length') });
            return false;
          }

          // Check for valid characters
          if (!/^[a-z0-9-]+$/.test(slug)) {
            setSlugValidation({
              isValid: false,
              isChecking: false,
              error: t('profile.slug.invalid-characters'),
            });
            return false;
          }

          setSlugValidation({ isValid: true, isChecking: true });

          try {
            const { data } = await checkSlugAvailability({
              variables: {
                slug,
                excludeUserId: currentUser?.user_id,
              },
            });

            const isAvailable = data?.checkSlugAvailability;
            if (isAvailable) {
              setSlugValidation({ isValid: true, isChecking: false });
              return true;
            } else {
              setSlugValidation({ isValid: false, isChecking: false, error: t('profile.slug.already-taken') });
              return false;
            }
          } catch (error) {
            setSlugValidation({ isValid: false, isChecking: false, error: t('profile.slug.check-error') });
            console.error(error);
            return false;
          }
        },
        [checkSlugAvailability, currentUser?.user_id]
      );

      // Simple debounce utility
      const useDebounce = (callback: (...args: any[]) => void, delay: number) => {
        const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

        return React.useCallback(
          (...args: any[]) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => callback(...args), delay);
          },
          [callback, delay]
        );
      };

      // Debounced slug validation
      const debouncedSlugValidation = useDebounce((slug: string) => {
        if (slug && slug !== profileData?.slug) {
          validateSlug(slug);
        } else {
          setSlugValidation({ isValid: true, isChecking: false });
        }
      }, 500);

      // Use formState.isDirty instead of watching all values for better performance

      // Only check for changes when form becomes dirty or when errors change
      React.useEffect(() => {
        if (isDirty) {
          const timeoutId = setTimeout(() => {
            onFormChange?.(true, Object.keys(errors).length > 0 || !slugValidation.isValid);
          }, 300); // Debounce for 300ms

          return () => clearTimeout(timeoutId);
        } else {
          // Only call onFormChange with false if we have baseline values (form is properly initialized)
          if (baselineValues) {
            onFormChange?.(false, Object.keys(errors).length > 0 || !slugValidation.isValid);
          }
        }
      }, [isDirty, errors, slugValidation, onFormChange, baselineValues]);

      const onSubmit = async (values: ProfileSettingsData) => {
        if (!currentUser && !isAdminMode) {
          throw new Error('Missing current user.');
        }

        // Check if slug is valid before submitting
        if (values.slug && !slugValidation.isValid) {
          toast.error(t('profile.slug.fix-errors-before-saving'), {
            position: 'bottom-right',
          });
          throw new Error('Invalid slug');
        }

        try {
          // In admin mode, skip internal mutations and just call onSave
          if (isAdminMode) {
            await onSave?.(values);
            return;
          }

          // Update user data - only include fields that have changed
          const userInput: any = {
            asfId: currentUser?.user_id,
          };

          if (values.firstName !== user?.firstName) userInput.firstName = values.firstName || null;
          if (values.lastName !== user?.lastName) userInput.lastName = values.lastName || null;
          if (values.story !== user?.story) userInput.story = values.story || null;

          // Only call updateUser if there are user fields to update
          if (Object.keys(userInput).length > 1) {
            // More than just asfId
            await updateUser({
              variables: { input: userInput },
            });
          }

          // Update profile data - only include fields that have changed
          const profileInput: any = {};
          // Use the most reliable source for original values - handle admin vs regular mode
          const profileData = isAdminMode ? user : user?.profile;
          const originalValues = {
            bio: profileData?.bio,
            displayName: profileData?.displayName,
            artistStatement: profileData?.artistStatement,
            country: profileData?.country,
            region: profileData?.region,
            city: profileData?.city,
            countryCode: profileData?.countryCode,
            regionCode: profileData?.regionCode,
            latitude: profileData?.latitude,
            longitude: profileData?.longitude,
            timezone: profileData?.timezone,
            slug: profileData?.slug,
            avatarUrl: profileData?.avatarUrl,
            bannerUrl: profileData?.bannerUrl,
            categories: profileData?.categories || [],
            socialLinks: profileData?.socialLinks || [],
            isPublic: profileData?.isPublic !== undefined ? profileData.isPublic : true,
            watermarkEnabled: profileData?.watermarkEnabled !== undefined ? profileData.watermarkEnabled : false,
            showMockups: profileData?.showMockups !== undefined ? profileData.showMockups : true,
            showArtworkSpecs: profileData?.showArtworkSpecs !== undefined ? profileData.showArtworkSpecs : true,
            showActivity: profileData?.showActivity !== undefined ? profileData.showActivity : true,
          };

          // Only include fields that have actually changed
          if (values.bio !== originalValues.bio) profileInput.bio = values.bio || null;
          if (values.displayName !== originalValues.displayName) profileInput.displayName = values.displayName || null;
          if (values.artistStatement !== originalValues.artistStatement)
            profileInput.artistStatement = values.artistStatement || null;
          if (values.country !== originalValues.country) profileInput.country = values.country || null;
          if (values.region !== originalValues.region) profileInput.region = values.region || null;
          if (values.city !== originalValues.city) profileInput.city = values.city || null;
          if (values.countryCode !== originalValues.countryCode) profileInput.countryCode = values.countryCode || null;
          if (values.regionCode !== originalValues.regionCode) profileInput.regionCode = values.regionCode || null;
          if (values.latitude !== originalValues.latitude) profileInput.latitude = values.latitude || null;
          if (values.longitude !== originalValues.longitude) profileInput.longitude = values.longitude || null;

          if (values.slug !== originalValues.slug) profileInput.slug = values.slug || null;
          if (values.avatarUrl !== originalValues.avatarUrl) profileInput.avatarUrl = values.avatarUrl || null;
          if (values.bannerUrl !== originalValues.bannerUrl) profileInput.bannerUrl = values.bannerUrl || null;
          if (values.isPublic !== originalValues.isPublic) profileInput.isPublic = values.isPublic;
          if (values.watermarkEnabled !== originalValues.watermarkEnabled)
            profileInput.watermarkEnabled = values.watermarkEnabled;
          if (values.showMockups !== originalValues.showMockups) profileInput.showMockups = values.showMockups;
          if (values.showArtworkSpecs !== originalValues.showArtworkSpecs)
            profileInput.showArtworkSpecs = values.showArtworkSpecs;
          if (values.showActivity !== originalValues.showActivity) profileInput.showActivity = values.showActivity;
          // More robust comparison for arrays - normalize to only compare relevant fields
          const normalizeCategories = (cats: any[]) =>
            cats?.map((cat) => ({
              categoryId: cat.categoryId || cat.id,
              isPrimary: cat.isPrimary,
            })) || [];

          const normalizeSocialLinks = (links: any[]) =>
            links?.map((link) => ({
              type: link.type,
              url: link.url,
              username: link.username,
              title: link.title,
              locked: link.locked,
            })) || [];

          const categoriesChanged =
            JSON.stringify(normalizeCategories(values.categories || [])) !==
            JSON.stringify(normalizeCategories(originalValues.categories || []));
          const socialLinksChanged =
            JSON.stringify(normalizeSocialLinks(values.socialLinks || [])) !==
            JSON.stringify(normalizeSocialLinks(originalValues.socialLinks || []));

          if (categoriesChanged) {
            profileInput.categories = values.categories || [];
          }
          if (socialLinksChanged) {
            // Filter out social links that don't have both type and (URL or username)
            // This prevents sending empty URLs to the backend
            const filteredSocialLinks = (values.socialLinks || [])
              .filter((link) => {
                if (!link.type) return false;
                const isUsernameOnly = USERNAME_ONLY_PLATFORMS.has(link.type);
                if (isUsernameOnly) {
                  return link.username && link.username.trim().length > 0;
                } else {
                  return link.url && link.url.trim().length > 0;
                }
              })
              .map(prepareSocialLinkForApi);
            profileInput.socialLinks = filteredSocialLinks;
          }

          // Only call updateProfile if there are profile fields to update
          if (Object.keys(profileInput).length > 0) {
            await updateProfile({
              variables: { input: profileInput },
            });
          }

          // Only refetch if we updated profile fields that might affect other parts of the app
          // Skip refetch if we only updated location fields (which don't affect profile picture)
          const hasNonLocationChanges = Object.keys(profileInput).some(
            (key) =>
              !['countryCode', 'country', 'regionCode', 'region', 'city', 'latitude', 'longitude', 'timezone'].includes(
                key
              )
          );

          if (hasNonLocationChanges && refetchProfile) {
            await refetchProfile();
          }

          toast.success(t('profile.save-success'), {
            position: 'bottom-right',
          });

          if (amplitude && intercom) {
            amplitude.track('profile_updated');
            intercom.trackEvent('profile_updated');
          }

          // Clear temp image URLs after successful save
          setTempImageUrls({});

          // Update baseline values to reflect the saved state
          // Use the actual values that were saved, not the form values
          const savedValues = {
            firstName: values.firstName || '',
            lastName: values.lastName || '',
            story: values.story || '',
            bio: values.bio || '',
            displayName: values.displayName || '',
            artistStatement: values.artistStatement || '',
            country: values.country || '',
            region: values.region || '',
            city: values.city || '',
            countryCode: values.countryCode || '',
            regionCode: values.regionCode || '',
            latitude: values.latitude || null,
            longitude: values.longitude || null,
            timezone: values.timezone || '',
            slug: values.slug || '',
            avatarUrl: values.avatarUrl || null,
            bannerUrl: values.bannerUrl || null,
            categories: values.categories || [],
            socialLinks: getDefaultSocialLinks(values.socialLinks || []),
            isPublic: values.isPublic,
            watermarkEnabled: values.watermarkEnabled,
            showMockups: values.showMockups,
            showArtworkSpecs: values.showArtworkSpecs,
            showActivity: values.showActivity,
          };

          setBaselineValues(savedValues);

          // Reset the form with the saved values to ensure consistency
          reset(savedValues);

          // Call the onSave callback if provided
          await onSave?.(values);
        } catch (error) {
          toast.error(t('profile.save-error'), {
            position: 'bottom-right',
          });
          console.error(error);
        }
      };

      const baseProfileUrl = getPublicSiteBaseUrl() + '/';

      // Custom submit handler that always calls onSave to reset loading state
      const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Always call onLoadingStateChange to reset loading state, regardless of validation
        const resetLoading = () => {
          onLoadingStateChange?.(false);
        };

        // Use handleSubmit - it's synchronous for validation
        // Pass the event to handleSubmit
        handleSubmit(
          async (data) => {
            try {
              await onSubmit(data);
              // Reset loading on successful submission
              resetLoading();
            } catch (error) {
              // Reset loading on submit error
              resetLoading();
              throw error;
            }
          },
          (validationErrors) => {
            // This is the onInvalid callback - validation failed
            // Reset loading state immediately when validation fails
            resetLoading();

            // Show toast with validation errors
            const socialLinkErrors = (validationErrors.socialLinks as any) || [];
            const hasSocialLinkErrors = Array.isArray(socialLinkErrors) && socialLinkErrors.some((err: any) => err);

            if (hasSocialLinkErrors) {
              toast.error(t('profile.social-links.fix-errors-before-saving'), {
                position: 'bottom-right',
                duration: 5000,
              });
            } else {
              toast.error(t('profile.form-fix-errors'), {
                position: 'bottom-right',
              });
            }
          }
        )(e);
      };

      return (
        <form ref={ref} onSubmit={handleFormSubmit} className="mx-auto w-full">
          <div className="space-y-10 md:space-y-6">
            {/* Profile Section */}
            <div className="bg-white md:border md:border-gray-200 rounded-md md:p-6">
              {/* Human-Made Art badge & toolkit CTA — links to the full Badge Studio on /verify */}
              {profileData?.isHumanVerified && profileData?.slug && (
                <a
                  href={`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/verify`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    amplitude?.track('verify_external_link_clicked', {
                      source: 'profile_settings_badge_cta',
                    })
                  }
                  className="group mb-6 flex items-center gap-4 rounded-xl border border-[#e7e2d6] bg-[#faf7f1] px-4 py-3 transition hover:border-green-600/40 hover:shadow-sm"
                >
                  <img src={HM_LOGO_URL} alt="" className="size-10 shrink-0 object-contain" />

                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-green-700">
                      {t('profile.badge-cta.eyebrow')}
                    </p>
                    <p className="text-sm font-semibold text-gray-900">{t('profile.badge-cta.title')}</p>
                  </div>

                  <ArrowRight className="size-4 shrink-0 text-green-700 transition-transform group-hover:translate-x-0.5" />
                </a>
              )}

              {location.pathname !== '/profile' && (
                <>
                  <h2 className="hidden md:block text-2xl font-bold mb-4">{t('profile.heading')}</h2>
                  <p className="text-sm text-gray-600 mb-6">{t('profile.description')}</p>
                </>
              )}

              <div className="space-y-8">
                {/* Public Profile and Show Mockups Toggles */}
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Public Profile Toggle */}
                  <div className="flex-1">
                    <Label htmlFor="isPublic" className="mb-2 block">
                      {t('profile.public-profile.label')}
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">{t('profile.public-profile.description')}</p>
                    <div className="flex items-center">
                      <Controller
                        name="isPublic"
                        control={control}
                        render={({ field }) => (
                          <Switch id="isPublic" checked={field.value ?? true} onCheckedChange={field.onChange} />
                        )}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {watch('isPublic') ? tc('public') : tc('private')}
                      </span>
                    </div>
                  </div>

                  {/* Show Mockups Toggle */}
                  <div className="flex-1">
                    <Label htmlFor="showMockups" className="mb-2 block">
                      {t('profile.show-mockups.label')}
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">{t('profile.show-mockups.description')}</p>
                    <div className="flex items-center">
                      <Controller
                        name="showMockups"
                        control={control}
                        render={({ field }) => (
                          <Switch id="showMockups" checked={field.value ?? true} onCheckedChange={field.onChange} />
                        )}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {watch('showMockups') ? tc('enabled') : tc('disabled')}
                      </span>
                    </div>
                  </div>

                  {/* Show Artwork Specs Toggle */}
                  <div className="flex-1">
                    <Label htmlFor="showArtworkSpecs" className="mb-2 block">
                      {t('profile.show-artwork-specs.label')}
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">{t('profile.show-artwork-specs.description')}</p>
                    <div className="flex items-center">
                      <Controller
                        name="showArtworkSpecs"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="showArtworkSpecs"
                            checked={field.value ?? true}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {watch('showArtworkSpecs') ? tc('enabled') : tc('disabled')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Show Activity Tab Toggle */}
                <div>
                  <Label htmlFor="showActivity" className="mb-2 block">
                    {t('profile.show-activity.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.show-activity.description')}</p>
                  <div className="flex items-center">
                    <Controller
                      name="showActivity"
                      control={control}
                      render={({ field }) => (
                        <Switch id="showActivity" checked={field.value ?? true} onCheckedChange={field.onChange} />
                      )}
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {watch('showActivity') ? tc('enabled') : tc('disabled')}
                    </span>
                  </div>
                </div>

                {/* Watermark Settings */}
                <div>
                  <Label htmlFor="watermarkEnabled" className="mb-2 block">
                    {t('profile.watermark.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.watermark.description')}</p>
                  <div className="flex items-center mb-4">
                    <Controller
                      name="watermarkEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch id="watermarkEnabled" checked={field.value ?? false} onCheckedChange={field.onChange} />
                      )}
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {watch('watermarkEnabled') ? tc('enabled') : tc('disabled')}
                    </span>
                  </div>

                  {watch('watermarkEnabled') && (
                    <WatermarkUploader
                      watermarkUrl={isAdminMode ? user?.watermarkUrl : user?.profile?.watermarkUrl}
                      refetchProfile={profileRefetch || undefined}
                      isAdminMode={isAdminMode}
                      targetUserId={isAdminMode ? user?.userId || user?.user?.asfId : undefined}
                      profileId={isAdminMode ? user?.id : undefined}
                    />
                  )}
                </div>

                {/* Profile Picture and Email */}
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-shrink-0">
                    <ImageUploadField
                      label={t('profile.avatar.label')}
                      description={t('profile.avatar.description')}
                      currentImageUrl={
                        tempImageUrls.avatarUrl ||
                        (watch('avatarUrl') !== undefined ? watch('avatarUrl') : user?.profile?.avatarUrl)
                      }
                      imageType="AVATAR"
                      onImageUploaded={(url) => {
                        setValue('avatarUrl', url, { shouldDirty: true });
                        setTempImageUrls((prev) => ({ ...prev, avatarUrl: url || undefined }));
                      }}
                      isAdminMode={isAdminMode}
                      targetUserId={isAdminMode ? user?.userId || user?.user?.asfId : undefined}
                    />
                  </div>
                  <div className="flex-1">
                    {/* Profile URL Slug */}

                    <Label htmlFor="slug" className="mb-2 block">
                      {t('profile.slug.label')}
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">{t('profile.slug.description')}</p>
                    <div className="flex">
                      <div className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-sm text-gray-600 whitespace-nowrap max-w-[180px]">
                        {baseProfileUrl.replace(/^https?:\/\//, '')}
                      </div>
                      <Input
                        id="slug"
                        placeholder={t('profile.slug.placeholder')}
                        className={`w-full rounded-l-none ${slugValidation.isValid === false ? 'border-red-500' : ''}`}
                        {...register('slug', {
                          onChange: (e) => {
                            const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            e.target.value = slug;
                            debouncedSlugValidation(slug);
                          },
                        })}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div>
                        {slugValidation.error && (
                          <p className="text-red-500 text-sm mt-1 flex items-center">
                            <Frown className="w-4 h-4 mr-1" /> {slugValidation.error}
                          </p>
                        )}
                        {slugValidation.isValid &&
                          !slugValidation.isChecking &&
                          watch('slug') &&
                          watch('slug') !== profileData?.slug && (
                            <p className="text-green-500 text-sm mt-1 flex items-center">
                              <Smile className="w-4 h-4 mr-1" /> {t('profile.slug.available')}
                            </p>
                          )}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <CopyButton text={`${baseProfileUrl}${watch('slug')}`} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <Label htmlFor="email" className="mb-2 block">
                    {t('profile.email.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.email.description')}</p>
                  <Input
                    id="email"
                    className="w-full"
                    value={isAdminMode ? user?.user?.email || '' : user?.email || ''}
                    disabled
                  />
                </div>

                {/* First Name and Last Name */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="firstName" className="mb-2 block">
                      {t('profile.first-name.label')}
                    </Label>
                    <Input
                      id="firstName"
                      placeholder={t('profile.first-name.placeholder')}
                      className="w-full"
                      {...register('firstName')}
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>}
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="lastName" className="mb-2 block">
                      {t('profile.last-name.label')}
                    </Label>
                    <Input
                      id="lastName"
                      placeholder={t('profile.last-name.placeholder')}
                      className="w-full"
                      {...register('lastName')}
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>}
                  </div>
                </div>

                {/* Display Name */}
                <div className="flex-1">
                  <Label htmlFor="displayName" className="mb-2 block">
                    {t('profile.display-name.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.display-name.description')}</p>
                  <Input
                    id="displayName"
                    placeholder={t('profile.display-name.placeholder')}
                    className="w-full"
                    {...register('displayName')}
                  />
                  <div className="flex items-center mt-1 gap-2">
                    <ContentGenerationPopover
                      inputField="profile_display_name"
                      open={displayNamePopoverOpen}
                      onOpenChange={setDisplayNamePopoverOpen}
                      currentValue={watch('displayName') || undefined}
                      onUse={(value) => {
                        setValue('displayName', value, { shouldDirty: true });
                      }}
                      onGenerate={async (suggestion, previousContent) => {
                        return await generateDisplayName(suggestion, previousContent);
                      }}
                      placeholder={t('profile.display-name.generating')}
                    />

                    {errors.displayName && <p className="text-red-500 text-sm">{errors.displayName.message}</p>}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <Label htmlFor="bio" className="mb-2 block">
                    {t('profile.bio.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.bio.description')}</p>
                  <Textarea
                    id="bio"
                    placeholder={t('profile.bio.placeholder', { max: BIO_MAX_LENGTH })}
                    className="w-full min-h-[80px] rounded-md"
                    maxLength={BIO_MAX_LENGTH}
                    {...register('bio')}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.bio && <p className="text-red-500 text-sm">{errors.bio.message}</p>}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <ContentGenerationPopover
                          inputField="profile_bio"
                          open={bioPopoverOpen}
                          onOpenChange={setBioPopoverOpen}
                          currentValue={watch('bio') || undefined}
                          onUse={(value) => {
                            setValue('bio', value, { shouldDirty: true });
                          }}
                          onGenerate={async (suggestion, previousContent) => {
                            return await generateBio(suggestion, previousContent);
                          }}
                          placeholder={t('profile.bio.generating')}
                          maxLength={BIO_MAX_LENGTH}
                        />
                        <CompactAudioRecorder
                          setValue={(value: string) => setValue('bio', value)}
                          className="!bg-transparent"
                        />
                      </div>
                      <div
                        className={`text-xs ${(watch('bio')?.length || 0) > BIO_MAX_LENGTH ? 'text-red-500' : 'text-gray-500'}`}
                      >
                        {watch('bio')?.length || 0}/{BIO_MAX_LENGTH}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Categories */}
                <div>
                  <CategorySelector
                    selectedCategories={(watch('categories') || []).map((cat: any) => ({
                      categoryId: cat.categoryId,
                      isPrimary: cat.isPrimary || false,
                    }))}
                    onCategoriesChange={(categories) => {
                      const formattedCategories = categories.map((cat) => ({
                        categoryId: cat.categoryId,
                        isPrimary: cat.isPrimary,
                      }));
                      setValue('categories', formattedCategories, { shouldDirty: true });
                    }}
                    maxCategories={6}
                    isLoading={isGeneratingCategories}
                    isModalOpen={categoryModalOpen}
                    onModalOpenChange={setCategoryModalOpen}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1 mt-2"
                    onClick={handleGenerateCategories}
                    disabled={isGeneratingCategories}
                  >
                    <Wand2 className="w-4 h-4" />
                  </Button>
                </div>

                <div>
                  <Label htmlFor="story" className="mb-2 block">
                    {t('profile.story.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.story.description')}</p>
                  <Textarea
                    id="story"
                    placeholder={t('profile.story.placeholder')}
                    className="w-full min-h-[150px] rounded-md"
                    maxLength={STORY_MAX_LENGTH}
                    {...register('story')}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.story && <p className="text-red-500 text-sm">{errors.story.message}</p>}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <ContentGenerationPopover
                          inputField="profile_story"
                          open={storyPopoverOpen}
                          onOpenChange={setStoryPopoverOpen}
                          currentValue={watch('story') || undefined}
                          onUse={(value) => {
                            setValue('story', value, { shouldDirty: true });
                          }}
                          onGenerate={async (suggestion, previousContent) => {
                            return await generateStory(suggestion, previousContent);
                          }}
                          placeholder={t('profile.story.generating')}
                          maxLength={STORY_MAX_LENGTH}
                        />
                        <CompactAudioRecorder
                          setValue={(value: string) => setValue('story', value)}
                          className="!bg-transparent"
                        />
                      </div>
                      <div
                        className={`text-xs ${(watch('story')?.length || 0) > STORY_MAX_LENGTH ? 'text-red-500' : 'text-gray-500'}`}
                      >
                        {watch('story')?.length || 0}/{STORY_MAX_LENGTH}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Fields */}
                <div>
                  <Label className="mb-2 block">{t('profile.location.label')}</Label>
                  <p className="text-xs text-gray-500 mb-3">{t('profile.location.description')}</p>
                  <LocationSelector
                    country={watch('country') || ''}
                    region={watch('region') || ''}
                    city={watch('city') || ''}
                    onCountryChange={(country) => {
                      if (country) {
                        setValue('country', country.name, { shouldDirty: true });
                      } else {
                        setValue('country', '', { shouldDirty: true });
                      }
                    }}
                    onRegionChange={(region) => {
                      if (region) {
                        setValue('region', region.name, { shouldDirty: true });
                      } else {
                        setValue('region', '', { shouldDirty: true });
                      }
                    }}
                    onCityChange={(city) => {
                      if (city) {
                        setValue('city', city.name, { shouldDirty: true });
                      } else {
                        setValue('city', '', { shouldDirty: true });
                      }
                    }}
                    onLocationDataChange={(data) => {
                      // Only the keys the selector sends: it states every level it
                      // changes (null to clear) and omits a parent it could not resolve.
                      Object.entries(data).forEach(([field, value]) => {
                        setValue(field as any, value, { shouldDirty: true });
                      });
                    }}
                  />
                </div>

                {/* Social Links Section */}
                <div className="overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="mb-2 block">{t('profile.links.label')}</Label>
                      <p className="text-xs text-gray-500 mb-2">{t('profile.links.description')}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        appendSocialLink({ type: '', url: '', title: '', locked: false });
                        // Trigger validation immediately after adding the field
                        trigger('socialLinks');
                      }}
                      className="flex items-center gap-2 rounded-full"
                    >
                      <Plus className="w-4 h-4" />
                      {t('profile.links.add')}
                    </Button>
                  </div>

                  <div className="space-y-3 overflow-hidden">
                    {socialLinkFields.map((field: any, index: number) => (
                      <SocialLinkField
                        key={field.id}
                        index={index}
                        onRemove={() => {
                          removeSocialLink(index);
                          // Trigger validation immediately after removing the field
                          trigger('socialLinks');
                        }}
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        locked={field.locked}
                      />
                    ))}
                  </div>
                </div>

                {/* Banner Image */}
                <div>
                  <ImageUploadField
                    label={t('profile.banner.label')}
                    description={t('profile.banner.description')}
                    currentImageUrl={
                      tempImageUrls.bannerUrl ||
                      (watch('bannerUrl') !== undefined ? watch('bannerUrl') : user?.profile?.bannerUrl)
                    }
                    imageType="BANNER"
                    onImageUploaded={(url) => {
                      setValue('bannerUrl', url, { shouldDirty: true });
                      setTempImageUrls((prev) => ({ ...prev, bannerUrl: url || undefined }));
                    }}
                    isAdminMode={isAdminMode}
                    targetUserId={isAdminMode ? user?.user?.user_id || user?.user_id : undefined}
                  />
                </div>

                {/* Artist Statement */}
                <div>
                  <Label htmlFor="artistStatement" className="mb-2 block">
                    {t('profile.artist-statement.label')}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">{t('profile.artist-statement.description')}</p>
                  <Textarea
                    id="artistStatement"
                    placeholder={t('profile.artist-statement.placeholder')}
                    className="w-full min-h-[150px] rounded-md"
                    maxLength={ARTIST_STATEMENT_MAX_LENGTH}
                    {...register('artistStatement')}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.artistStatement && <p className="text-red-500 text-sm">{errors.artistStatement.message}</p>}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <ContentGenerationPopover
                          inputField="profile_artist_statement"
                          open={artistStatementPopoverOpen}
                          onOpenChange={setArtistStatementPopoverOpen}
                          currentValue={watch('artistStatement') || undefined}
                          onUse={(value) => {
                            setValue('artistStatement', value, { shouldDirty: true });
                          }}
                          onGenerate={async (suggestion, previousContent) => {
                            return await generateArtistStatement(suggestion, previousContent);
                          }}
                          placeholder={t('profile.artist-statement.generating')}
                          maxLength={ARTIST_STATEMENT_MAX_LENGTH}
                        />
                        <CompactAudioRecorder
                          setValue={(value: string) => setValue('artistStatement', value)}
                          className="!bg-transparent"
                        />
                      </div>
                      <div
                        className={`text-xs ${(watch('artistStatement')?.length || 0) > ARTIST_STATEMENT_MAX_LENGTH ? 'text-red-500' : 'text-gray-500'}`}
                      >
                        {watch('artistStatement')?.length || 0}/{ARTIST_STATEMENT_MAX_LENGTH}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      );
    }
  )
);

export default ProfileSettings;
